import dayjs from 'dayjs';
import { DeliveryType, ICartAvailableDelivery, Service } from '@features/cart/typescript';
import type { CartTaxGroup, CartTaxesTotalGroup, CartTaxesTotalType } from '@features/cart/typescript';
import { IAvailableServicePositionV2, IAvailableServiceV2, ICartDeliveriesV2Response, IDeliveryItemV2, IDeliveryPricing, IDeliveryPriceWithTaxRate, IPricingTableData } from '@features/checkout/typescript';
import { ASSEMBLY_SERVICE_VAT_23_ID, ASSEMBLY_SERVICE_VAT_8_ID, EXPRESS_SERVICE_TYPE } from '@features/cart/const';
import { countBusinessDays } from '@utils/addBusinessDays';
const normalizeTaxRate = (taxRate?: number | string): string => String(taxRate ?? '');
const isReducedAssemblyVat = (taxRate: string | undefined): boolean => normalizeTaxRate(taxRate) === '8';
const getPositionTaxRates = (positions: IAvailableServicePositionV2[]): Set<string> => new Set(positions.map(position => normalizeTaxRate(position.price.gross.taxRate)).filter(taxRate => taxRate !== '' && taxRate !== '0'));
const hasMultiplePositionTaxRates = (positions: IAvailableServicePositionV2[]): boolean => getPositionTaxRates(positions).size > 1;
const serviceUsesPerPositionTaxRates = (service: IAvailableServiceV2): boolean => service.positionsTaxRate?.toUpperCase() === 'MIXED' || (service.positions?.length ? hasMultiplePositionTaxRates(service.positions) : false);
export const isMixedAssemblyTaxRate = (servicesPool: IAvailableServiceV2[], assemblyServiceId: string): boolean => {
  const assemblyService = servicesPool.find(service => service.serviceId === assemblyServiceId);
  if (!assemblyService?.positions?.length) {
    return false;
  }
  return serviceUsesPerPositionTaxRates(assemblyService);
};
export type CartProductPriceInput = {
  positionId: string;
  assemblyAmount: number;
  grossAmount: number;
  productTaxRate?: string;
  isStorProduct?: boolean;
};
const getPositionTaxRatesFromService = (service: IAvailableServiceV2 | undefined): Map<string, string> => {
  const taxRateByPosition = new Map<string, string>();
  if (!service?.positions?.length) {
    return taxRateByPosition;
  }
  const usePerPositionRates = serviceUsesPerPositionTaxRates(service);
  service.positions.forEach(position => {
    const positionTaxRate = normalizeTaxRate(position.price.gross.taxRate);
    const taxRate = usePerPositionRates ? positionTaxRate || normalizeTaxRate(service.serviceTaxRate) : normalizeTaxRate(service.serviceTaxRate);
    taxRateByPosition.set(position.positionId, taxRate);
  });
  return taxRateByPosition;
};
const getProductPriceByTaxRate = (product: CartProductPriceInput, taxRate: string | undefined): number => {
  if (isReducedAssemblyVat(taxRate)) {
    return product.assemblyAmount;
  }
  return product.grossAmount;
};
const resolveProductTaxRate = (product: CartProductPriceInput, taxRateByPosition: Map<string, string>, isMixedService: boolean): string | undefined => {
  const serviceTaxRate = taxRateByPosition.get(product.positionId);
  if (serviceTaxRate) {
    return serviceTaxRate;
  }
  if (isMixedService) {
    return '23';
  }
  return undefined;
};
export type AssemblyProductsPriceOptions = {
  isMixedCart?: boolean;
};
export const calculateAssemblyProductsPriceFromServices = (servicesPool: IAvailableServiceV2[], products: CartProductPriceInput[], assemblyServiceId: string, options: AssemblyProductsPriceOptions = {}): number => {
  const assemblyService = servicesPool.find(service => service.serviceId === assemblyServiceId);
  const taxRateByPosition = getPositionTaxRatesFromService(assemblyService);
  const hasUnmatchedProducts = products.some(product => !taxRateByPosition.has(product.positionId));
  const isMixedService = options.isMixedCart || (assemblyService ? serviceUsesPerPositionTaxRates(assemblyService) : false) || hasUnmatchedProducts;
  if (!taxRateByPosition.size && !isMixedService) {
    return 0;
  }
  return products.reduce((sum, product) => {
    const taxRate = resolveProductTaxRate(product, taxRateByPosition, isMixedService);
    if (!taxRate) {
      return sum;
    }
    return sum + getProductPriceByTaxRate(product, taxRate);
  }, 0);
};
export const calculateSelfAssemblyProductsPrice = (products: CartProductPriceInput[]): number => products.reduce((sum, product) => sum + product.grossAmount, 0);
export const calculateMixedAssemblyProductsPriceFromCart = (products: CartProductPriceInput[]): number => products.reduce((sum, product) => sum + (product.isStorProduct ? product.assemblyAmount : product.grossAmount), 0);
export type MixedVatProductsBreakdown = {
  products8: number;
  products23: number;
};
export type MixedVatAssemblyBreakdown = {
  assembly8: number;
  assembly23: number;
};
export type AssemblyPriceComponent = {
  gross: {
    amount: number;
    taxRate: number | string;
  };
};
const getTaxesTotalAmount = (taxesTotals: CartTaxesTotalGroup[], taxRate: string, totalType: CartTaxesTotalType): number => {
  const group = taxesTotals.find(entry => String(entry.taxRate) === taxRate);
  const totalEntry = group?.totals.find(entry => entry.totalType === totalType);
  return totalEntry?.total.amount ?? 0;
};
export const getMixedVatAssemblyBreakdownFromComponents = (components: AssemblyPriceComponent[] | undefined): MixedVatAssemblyBreakdown => (components ?? []).reduce<MixedVatAssemblyBreakdown>((acc, component) => {
  const taxRate = normalizeTaxRate(component.gross.taxRate);
  if (isReducedAssemblyVat(taxRate)) {
    acc.assembly8 += component.gross.amount;
  } else {
    acc.assembly23 += component.gross.amount;
  }
  return acc;
}, {
  assembly8: 0,
  assembly23: 0
});
export const getMixedVatBreakdownFromTaxesTotals = (taxesTotals: CartTaxesTotalGroup[], taxes: CartTaxGroup[] | undefined, useBasePrices: boolean, assemblyComponents?: AssemblyPriceComponent[]): (MixedVatProductsBreakdown & MixedVatAssemblyBreakdown) | null => {
  if (!taxesTotals.length) {
    return null;
  }
  let products8 = getTaxesTotalAmount(taxesTotals, '8', 'products');
  let products23 = getTaxesTotalAmount(taxesTotals, '23', 'products');

  // Panel podsumowania pokazuje kwoty przed obniżką (Obniżka jest osobnym wierszem).
  // taxesTotals.products to wartości po obniżkach — przy useBasePrices bierzemy totalPriceV2.base.
  if (useBasePrices && taxes?.length) {
    const taxGroup8 = taxes.find(entry => String(entry.taxRate) === '8');
    const taxGroup23 = taxes.find(entry => String(entry.taxRate) === '23');
    const base8 = taxGroup8?.totalPriceV2?.base?.assembly?.sum?.amount;
    const base23 = taxGroup23?.totalPriceV2?.base?.gross?.sum?.amount;

    // base może być jeszcze sprzed split 8/23 (products8=0) — wtedy fallback do kalkulacji klienta.
    if ((base8 == null || base8 === 0) && products8 > 0) {
      return null;
    }
    if (base8 != null) {
      products8 = base8;
    }
    if (base23 != null) {
      products23 = base23;
    }
  }
  let {
    assembly8,
    assembly23
  } = {
    assembly8: getTaxesTotalAmount(taxesTotals, '8', 'assembly_services'),
    assembly23: getTaxesTotalAmount(taxesTotals, '23', 'assembly_services')
  };
  if (assembly8 === 0 && assembly23 === 0 && assemblyComponents?.length) {
    ({
      assembly8,
      assembly23
    } = getMixedVatAssemblyBreakdownFromComponents(assemblyComponents));
  }

  // API product split not ready yet (assembly already @8%, products still all @23%).
  if (assembly8 > 0 && products8 === 0) {
    return null;
  }
  const hasReducedVat = products8 > 0 || assembly8 > 0;
  const hasStandardVat = products23 > 0 || assembly23 > 0;
  if (!hasReducedVat || !hasStandardVat) {
    return null;
  }
  return {
    products8,
    products23,
    assembly8,
    assembly23
  };
};
export const calculateMixedVatProductsBreakdown = (products: CartProductPriceInput[], servicesPool: IAvailableServiceV2[], assemblyServiceId: string, options: AssemblyProductsPriceOptions = {}): MixedVatProductsBreakdown => {
  const assemblyService = servicesPool.find(service => service.serviceId === assemblyServiceId);
  const taxRateByPosition = getPositionTaxRatesFromService(assemblyService);
  const hasUnmatchedProducts = products.some(product => !taxRateByPosition.has(product.positionId));
  const isMixedService = options.isMixedCart || (assemblyService ? serviceUsesPerPositionTaxRates(assemblyService) : false) || hasUnmatchedProducts;
  if (!taxRateByPosition.size && !isMixedService) {
    return {
      products8: 0,
      products23: 0
    };
  }
  if (options.isMixedCart && !taxRateByPosition.size) {
    return products.reduce<MixedVatProductsBreakdown>((acc, product) => {
      if (product.isStorProduct) {
        acc.products8 += product.assemblyAmount;
      } else {
        acc.products23 += product.grossAmount;
      }
      return acc;
    }, {
      products8: 0,
      products23: 0
    });
  }
  return products.reduce<MixedVatProductsBreakdown>((acc, product) => {
    const taxRate = resolveProductTaxRate(product, taxRateByPosition, isMixedService);
    if (!taxRate) {
      return acc;
    }
    const price = getProductPriceByTaxRate(product, taxRate);
    if (isReducedAssemblyVat(taxRate)) {
      acc.products8 += price;
    } else {
      acc.products23 += price;
    }
    return acc;
  }, {
    products8: 0,
    products23: 0
  });
};
const isProductEligibleForMixed8Vat = (product: CartProductPriceInput, taxRateByPosition: Map<string, string>, isMixedService: boolean, isMixedCart: boolean): boolean => {
  if (isMixedCart && !taxRateByPosition.size) {
    return !!product.isStorProduct;
  }
  const taxRate = resolveProductTaxRate(product, taxRateByPosition, isMixedService);
  return !!taxRate && isReducedAssemblyVat(taxRate);
};

/** Porównanie cen produktów kwalifikujących się do mixed 8%: przed (23% brutto) vs po (8% z montażem). */
export const calculateMixedEligibleProductsPriceComparison = (products: CartProductPriceInput[], servicesPool: IAvailableServiceV2[], assemblyServiceId: string, options: AssemblyProductsPriceOptions = {}): {
  productsAt23BeforeAssembly: number;
  productsAt8: number;
} => {
  const assemblyService = servicesPool.find(service => service.serviceId === assemblyServiceId);
  const taxRateByPosition = getPositionTaxRatesFromService(assemblyService);
  const hasUnmatchedProducts = products.some(product => !taxRateByPosition.has(product.positionId));
  const isMixedService = options.isMixedCart || (assemblyService ? serviceUsesPerPositionTaxRates(assemblyService) : false) || hasUnmatchedProducts;
  const isMixedCart = !!options.isMixedCart;
  const {
    products8
  } = calculateMixedVatProductsBreakdown(products, servicesPool, assemblyServiceId, options);
  const productsAt23BeforeAssembly = products.reduce((sum, product) => {
    if (!isProductEligibleForMixed8Vat(product, taxRateByPosition, isMixedService, isMixedCart)) {
      return sum;
    }
    return sum + product.grossAmount;
  }, 0);
  return {
    productsAt23BeforeAssembly,
    productsAt8: products8
  };
};
export const calculateMixedVatAssemblyBreakdown = (servicesPool: IAvailableServiceV2[], assemblyServiceId: string): MixedVatAssemblyBreakdown => {
  const assemblyService = servicesPool.find(service => service.serviceId === assemblyServiceId);
  if (!assemblyService) {
    return {
      assembly8: 0,
      assembly23: 0
    };
  }
  if (assemblyService.positions?.length) {
    return assemblyService.positions.reduce<MixedVatAssemblyBreakdown>((acc, position) => {
      const taxRate = normalizeTaxRate(position.price.gross.taxRate || assemblyService.serviceTaxRate);
      const amount = position.price.gross.amount;
      if (isReducedAssemblyVat(taxRate)) {
        acc.assembly8 += amount;
      } else {
        acc.assembly23 += amount;
      }
      return acc;
    }, {
      assembly8: 0,
      assembly23: 0
    });
  }
  return assemblyService.total.reduce<MixedVatAssemblyBreakdown>((acc, entry) => {
    const taxRate = normalizeTaxRate(entry.taxRate);
    const amount = entry.gross.amount;
    if (isReducedAssemblyVat(taxRate)) {
      acc.assembly8 += amount;
    } else {
      acc.assembly23 += amount;
    }
    return acc;
  }, {
    assembly8: 0,
    assembly23: 0
  });
};
const getAvailableServiceGrossPrice = (service: IAvailableServiceV2): number => service.total.reduce((sum, entry) => sum + entry.gross.amount, 0);
export const getBringServiceGrossPriceFromPool = (servicesPool: IAvailableServiceV2[], preferReducedVat: boolean): number => {
  const bringServices = servicesPool.filter(service => service.serviceType?.toLowerCase() === 'bring');
  const match = bringServices.find(service => {
    const taxRate = normalizeTaxRate(service.serviceTaxRate);
    return preferReducedVat ? isReducedAssemblyVat(taxRate) : !isReducedAssemblyVat(taxRate);
  });
  return match ? getAvailableServiceGrossPrice(match) : 0;
};

/**
 * Dopłata za mixed montaż: (produkty 8%+23% + montaż 8%+23% + wniesienie 8%)
 * − (wszystkie produkty po 23% + wniesienie 23%). Izoluje koszt dodania montażu mixed.
 */
export const calculateMixedAssemblyDisplayPrice = (products: CartProductPriceInput[], servicesPool: IAvailableServiceV2[], options: AssemblyProductsPriceOptions = {}): number | null => {
  if (!products.length || !servicesPool.length) {
    return null;
  }
  const productsAt23Total = calculateSelfAssemblyProductsPrice(products);
  const bringAt23 = getBringServiceGrossPriceFromPool(servicesPool, false);
  const totalWithoutAssembly = productsAt23Total + bringAt23;
  const {
    products8,
    products23
  } = calculateMixedVatProductsBreakdown(products, servicesPool, ASSEMBLY_SERVICE_VAT_8_ID, options);
  const {
    assembly8,
    assembly23
  } = calculateMixedVatAssemblyBreakdown(servicesPool, ASSEMBLY_SERVICE_VAT_8_ID);
  const bringAt8 = getBringServiceGrossPriceFromPool(servicesPool, true);
  const totalWithMixedAssembly = products8 + products23 + assembly8 + assembly23 + bringAt8;
  return totalWithMixedAssembly - totalWithoutAssembly;
};
export type MixedAssemblySavingsMixedTotals = {
  products8: number;
  products23: number;
  assembly8: number;
  assembly23: number;
  bring: number;
};

/**
 * Oszczędność przy mixed montażu:
 * (wszystkie produkty 23% + montaż 23% + wniesienie 23%) − (produkty 8%+23% + montaż mixed 8%+23% + wniesienie 8%).
 */
export const calculateMixedAssemblySavings = (products: CartProductPriceInput[], servicesPool: IAvailableServiceV2[], options: AssemblyProductsPriceOptions = {}, mixedTotalsOverride?: MixedAssemblySavingsMixedTotals): number | null => {
  if (!products.length || !servicesPool.length) {
    return null;
  }
  const productsAt23Total = calculateSelfAssemblyProductsPrice(products);
  const assembly23Service = servicesPool.find(service => service.serviceId === ASSEMBLY_SERVICE_VAT_23_ID);
  const assembly23Full = assembly23Service ? getAvailableServiceGrossPrice(assembly23Service) : 0;
  const bringAt23 = getBringServiceGrossPriceFromPool(servicesPool, false);
  const scenarioFull23 = productsAt23Total + assembly23Full + bringAt23;
  let mixedScenario: number;
  if (mixedTotalsOverride) {
    mixedScenario = mixedTotalsOverride.products8 + mixedTotalsOverride.products23 + mixedTotalsOverride.assembly8 + mixedTotalsOverride.assembly23 + mixedTotalsOverride.bring;
  } else {
    const {
      products8,
      products23
    } = calculateMixedVatProductsBreakdown(products, servicesPool, ASSEMBLY_SERVICE_VAT_8_ID, options);
    const {
      assembly8,
      assembly23
    } = calculateMixedVatAssemblyBreakdown(servicesPool, ASSEMBLY_SERVICE_VAT_8_ID);
    const bringAt8 = getBringServiceGrossPriceFromPool(servicesPool, true);
    mixedScenario = products8 + products23 + assembly8 + assembly23 + bringAt8;
  }
  return scenarioFull23 - mixedScenario;
};

/** Różnica: montaż VOX (produkty 8% + montaż 8%) − samodzielny montaż (produkty 23% kwalifikujące się do 8%). */
export const getVoxVsSelfAssemblyDifferenceFromPricingData = (pricingData: IPricingTableData): number | null => {
  const assemblyPriceForTable = pricingData.assemblyPrice8 ?? pricingData.assemblyPrice;
  if (assemblyPriceForTable == null) {
    return null;
  }
  const assemblyProductsForTable = pricingData.mixedEligibleProductsAt8Price ?? pricingData.assemblyProductsPrice;
  const selfAssemblyProductsForTable = pricingData.mixedEligibleProductsAt23Price ?? pricingData.selfAssemblyProductsPrice;
  const voxTeamTotal = pricingData.mixedEligibleProductsAt8Price != null ? assemblyProductsForTable + assemblyPriceForTable : pricingData.assemblyTotalPrice + assemblyPriceForTable;
  const selfAssemblyTotal = pricingData.mixedEligibleProductsAt23Price != null ? selfAssemblyProductsForTable : pricingData.selfAssemblyTotalPrice;
  return voxTeamTotal - selfAssemblyTotal;
};
const parseTaxRate = (taxRate: string | number | undefined, fallback = 0): number => {
  if (taxRate === undefined) return fallback;
  return typeof taxRate === 'string' ? parseInt(taxRate, 10) : taxRate;
};
const toDeliveryPricing = (price: IDeliveryPriceWithTaxRate['price'], taxRate?: string | number): IDeliveryPricing => {
  const netTaxRate = price.net.taxRate ?? parseTaxRate(taxRate, 0);
  const grossTaxRate = price.gross.taxRate ?? parseTaxRate(taxRate, 23);
  return {
    net: {
      amount: price.net.amount,
      currency: price.net.currency,
      taxRate: netTaxRate
    },
    gross: {
      amount: price.gross.amount,
      currency: price.gross.currency,
      taxRate: grossTaxRate
    }
  };
};
export const resolvePricingWithRequiredServices = (deliveryItem: IDeliveryItemV2): IDeliveryPricing => {
  const requiredPricing = deliveryItem.pricing.withServices.required[0];
  if (requiredPricing) {
    return toDeliveryPricing(requiredPricing.price, requiredPricing.serviceTaxRate);
  }
  return toDeliveryPricing({
    net: deliveryItem.pricing.base.net,
    gross: deliveryItem.pricing.base.gross
  }, deliveryItem.pricing.base.gross.taxRate);
};
export const resolveDeliveryServices = (deliveryItem: IDeliveryItemV2, servicesPool: IAvailableServiceV2[]): Service[] => {
  if (!deliveryItem.services?.length) {
    return [];
  }
  const poolById = new Map(servicesPool.map(service => [service.serviceId, service]));
  return deliveryItem.services.filter(ref => poolById.has(ref.serviceId)).map(ref => ({
    serviceType: ref.serviceType,
    serviceId: ref.serviceId,
    price: {
      amount: ref.price.gross.amount,
      currency: ref.price.gross.currency,
      taxRate: ref.serviceTaxRate
    }
  }));
};
export const filterAssemblyServices = (services: Service[]): Service[] => services.filter(service => service.serviceType === 'assembly');
export const filterExpressServices = (services: Service[]): Service[] => services.filter(service => service.serviceType === EXPRESS_SERVICE_TYPE);
export const filterBringingServicesByAssemblyVat = (services: Service[], selectedAssemblyServices: Service[]): Service[] => {
  const isAssembly8VatSelected = selectedAssemblyServices.some(service => service.price.taxRate.toString() === '8');
  return services.filter(service => service.serviceType === 'bring').filter(service => isAssembly8VatSelected && service.price.taxRate.toString() === '8' || !isAssembly8VatSelected && service.price.taxRate.toString() !== '8');
};
export const convertDeliveryItemV2ToCartAvailableDelivery = (deliveryItem: IDeliveryItemV2, postalCode = '', servicesPool: IAvailableServiceV2[] = []): ICartAvailableDelivery => {
  const {
    options
  } = deliveryItem.configuration;
  return {
    deliveryType: 'delivery' as DeliveryType,
    deliveryTypeId: deliveryItem.deliveryId,
    deliveryTypeNameLanguageKey: deliveryItem.deliveryNameLanguageKey,
    isAssembleAvailable: options.assemblyServiceAvailable,
    isBringingAvailable: options.bringServiceAvailable,
    isExpressAvailable: options.expressDeliveryServiceAvailable ?? options.expressServiceAvailable,
    isPaidOnDelivery: options.paidOnDelivery,
    isTaxAvailable: options.allowToSetTransportGroupTaxRate,
    isVatAvailable: options.allowToSetTransportGroupTaxRate,
    leadTime: 0,
    logisticsTime: countBusinessDays(dayjs(), dayjs(deliveryItem.expectedDeliveryDate)),
    personalPickupStoreType: deliveryItem.personalPickupStoreType ?? null,
    price: {
      amount: deliveryItem.pricing.base.gross.amount,
      currency: deliveryItem.pricing.base.gross.currency
    },
    postalCode,
    configuration: {
      additionalFields: deliveryItem.configuration.additionalFields,
      requirements: deliveryItem.configuration.requirements || null
    },
    pricingWithRequiredServices: resolvePricingWithRequiredServices(deliveryItem),
    expectedDeliveryDate: deliveryItem.expectedDeliveryDate,
    availableServices: resolveDeliveryServices(deliveryItem, servicesPool)
  };
};
export const convertAvailableServiceV2ToService = (service: IAvailableServiceV2): Service => {
  const grossAmount = service.total.reduce((sum, entry) => sum + entry.gross.amount, 0);
  const currency = service.total[0]?.gross.currency ?? 'PLN';
  return {
    serviceType: service.serviceType,
    serviceId: service.serviceId,
    price: {
      amount: grossAmount,
      currency,
      taxRate: service.serviceTaxRate
    }
  };
};
export const getDeliveriesForTransportGroup = (response: ICartDeliveriesV2Response, cartId: string, transportGroupId: string): IDeliveryItemV2[] | undefined => {
  const {
    availableDeliveries
  } = response;
  return availableDeliveries[cartId] ?? availableDeliveries[transportGroupId];
};
export const getServicesForTransportGroup = (response: ICartDeliveriesV2Response, cartId: string, transportGroupId: string): Service[] => {
  const services = response.availableServices[cartId] ?? response.availableServices[transportGroupId];
  if (!Array.isArray(services)) {
    return [];
  }
  return services.map(convertAvailableServiceV2ToService);
};